/**
 * Claude inside ClientFlow: Draft reply, Prep brief, Summarize and Suggest categories. Shown only
 * when AI is set up and on for this person; each needs the client's AI consent (the server says
 * so if it's missing). Nothing here changes anything by itself.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileText, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { toast } from "@/lib/use-toast";

/** Whether to show the AI buttons: AI set up on the server, on for the firm, and for this person. */
export function useAi(): boolean {
  const { data } = useQuery<{ aiConfigured: boolean; meAllowed: boolean }>({ queryKey: ["/api/ai/settings"], staleTime: 60_000 });
  return !!data?.aiConfigured && !!data.meAllowed;
}

/** Fills the reply box with Claude's draft, for the person to edit and send. */
export function DraftReplyButton({ threadId, onDraft }: { threadId: string; onDraft: (text: string) => void }) {
  const on = useAi();
  const draft = useMutation({
    mutationFn: () => api.send<{ draft: string }>("POST", "/api/ai/assist/reply", { threadId }),
    onSuccess: (r) => (onDraft(r.draft), toast({ title: "Draft ready", description: "Read it and edit it before sending." })),
    onError: (e) => toast({ title: "No draft", description: errorMessage(e) }),
  });
  if (!on) return null;
  return (
    <Button type="button" size="sm" variant="outline" disabled={draft.isPending} onClick={() => draft.mutate()}>
      <Sparkles className="h-4 w-4" /> {draft.isPending ? "Drafting…" : "Draft reply"}
    </Button>
  );
}

/** A one-page brief before a meeting. */
export function MeetingBriefButton({ bookingId, title }: { bookingId: string; title: string }) {
  const on = useAi();
  const [open, setOpen] = useState(false);
  // Errors show in the dialog only (no second, global message).
  const brief = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send<{ sections: Array<{ heading: string; points: string[] }> }>("POST", "/api/ai/assist/meeting-brief", { bookingId }) });
  if (!on) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => (setOpen(true), brief.mutate())}>
        <FileText className="h-4 w-4" /> Prep brief
      </Button>
      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>Brief: {title}</DialogTitle>
            </DialogHeader>
            {brief.isPending ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4 animate-pulse" /> Claude is reading the client's file…
              </p>
            ) : brief.error ? (
              <p className="text-sm text-destructive">{errorMessage(brief.error)}</p>
            ) : (
              <div className="space-y-4 text-sm">
                {brief.data?.sections.map((s) => (
                  <section key={s.heading}>
                    <h3 className="mb-1 font-semibold">{s.heading}</h3>
                    <ul className="list-disc space-y-1 pl-5">
                      {s.points.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  </section>
                ))}
                <p className="border-t border-border pt-3 text-xs text-muted-foreground">Drafted by Claude from ClientFlow's records. Check anything important against the client's file.</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

type Summary = { summary: string; nextSteps: Array<{ kind: "task" | "request"; title: string; detail: string }> };

/** Where things stand with a client, and next steps to send to AI Review. */
export function ClientSummary({ clientId }: { clientId: string }) {
  const on = useAi();
  const [picked, setPicked] = useState<number[]>([]);
  const run = useMutation({ mutationFn: () => api.send<Summary>("POST", "/api/ai/assist/client-summary", { clientId }), onSuccess: (r) => setPicked(r.nextSteps.map((_, i) => i)) });
  const send = useMutation({
    mutationFn: () => api.send<{ proposed: number }>("POST", "/api/ai/assist/next-steps", { clientId, steps: run.data!.nextSteps.filter((_, i) => picked.includes(i)) }),
    onSuccess: (r) => toast({ title: `${r.proposed} sent to Review`, description: "Approve them there." }),
  });
  if (!on) return null;
  return (
    <div className="space-y-3 border-t border-border pt-3">
      <Button size="sm" variant="outline" disabled={run.isPending} onClick={() => run.mutate()}>
        <Sparkles className="h-4 w-4" /> {run.isPending ? "Summarizing…" : run.data ? "Summarize again" : "Summarize with Claude"}
      </Button>
      {run.error && <p className="text-sm text-destructive">{errorMessage(run.error)}</p>}
      {run.data && (
        <div className="space-y-3 rounded-md bg-muted/40 p-3 text-sm">
          <p className="whitespace-pre-wrap">{run.data.summary}</p>
          {run.data.nextSteps.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Suggested next steps</p>
              {run.data.nextSteps.map((st, i) => (
                <label key={i} className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0" checked={picked.includes(i)} onChange={(e) => setPicked((x) => (e.target.checked ? [...x, i] : x.filter((y) => y !== i)))} />
                  <span>
                    <span className="font-medium">{st.kind === "request" ? `Ask the client for: ${st.title}` : st.title}</span>
                    {st.detail && <span className="block text-xs text-muted-foreground">{st.detail}</span>}
                  </span>
                </label>
              ))}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" disabled={!picked.length || send.isPending || send.isSuccess} onClick={() => send.mutate()}>
                  Send {picked.length} to Review
                </Button>
                {send.isSuccess && (
                  <Link className="text-xs text-primary hover:underline" href="/ai">
                    Open Review
                  </Link>
                )}
              </div>
              {send.error && <p className="text-destructive">{errorMessage(send.error)}</p>}
            </div>
          )}
          <p className="text-xs text-muted-foreground">Drafted by Claude from ClientFlow's records.</p>
        </div>
      )}
    </div>
  );
}

/** Suggestions for the period's uncategorized transactions, as one batch in AI Review. */
export function SuggestCategoriesButton({ clientId, from, to }: { clientId: string; from: string; to: string }) {
  const on = useAi();
  const run = useMutation({
    mutationFn: () => api.send<{ proposed: number; skipped: number; message?: string }>("POST", "/api/ai/assist/categorize", { clientId, from, to }),
    onSuccess: (r) => toast({ title: r.message ?? `${r.proposed} suggestion${r.proposed === 1 ? "" : "s"} in Review`, description: r.message ? undefined : `${r.skipped ? `${r.skipped} left for you (Claude wasn't sure). ` : ""}Approve them in AI Review.` }),
    onError: (e) => toast({ title: "No suggestions", description: errorMessage(e) }),
  });
  if (!on) return null;
  return (
    <Button type="button" size="sm" variant="outline" disabled={run.isPending} onClick={() => run.mutate()}>
      <Sparkles className="h-4 w-4" /> {run.isPending ? "Suggesting…" : "Suggest categories"}
    </Button>
  );
}
